import React from "react";
import { NavLink } from "react-router-dom";
import {
  Building2, Users2, ListChecks, Bell, Paintbrush, IdCard, FileSignature,
  ShieldAlert, HardHat, Plug, ShieldCheck, FileBarChart, CreditCard,
  KeyRound, Trash2,
  Settings as SettingsIcon,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import OrgScopeEmptyState from "@/components/OrgScopeEmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgScope } from "@/hooks/useOrgScope";

/**
 * v1.2 — Settings page chrome.
 *
 *   <SettingsLayout>{pageContent}</SettingsLayout>
 *
 * v1.2 PR-C — consolidated to 15 tabs (folded 2+2+2 sub-pages).
 *
 * v1.2 PR-C.1 — tab list trimmed to 14: "Sites" removed (it's an
 * operational sidebar entry, not a config tab — duplicating it here
 * caused users to click and lose the settings rail). Audit log, Billing,
 * Notifications log, Users still live OUTSIDE this layout but are linked
 * from the rail; each of those pages now uses `useOrgScope()` to skip
 * fetches when the viewer is a super_admin with `org_id=null` so they
 * render the shared `<OrgScopeEmptyState/>` (with inline org picker)
 * instead of a "No org context" toast.
 *
 * Org-scope guard: when `noOrgSuperAdmin` is true, we render the shared
 * empty-state inside the content slot. The page itself NEVER mounts its
 * loader because each page also calls `useOrgScope()` and gates its
 * `useEffect` — defence-in-depth.
 */

const ROLE_LEVEL = { host: 1, receptionist: 2, site_manager: 3, org_admin: 4, super_admin: 5 };

const TABS = [
  { to: "/settings",                       end: true, label: "Organisation",          icon: Building2,    minRole: "host",         testid: "stab-org" },
  { to: "/users",                                     label: "Users & invitations",    icon: Users2,       minRole: "org_admin",    testid: "stab-users" },
  { to: "/settings/visitor-types",                    label: "Visitor types & fields", icon: IdCard,       minRole: "host",         testid: "stab-visitor-types" },
  { to: "/settings/notifications",                    label: "Notifications",          icon: Bell,         minRole: "host",         testid: "stab-notifications" },
  { to: "/settings/branding",                         label: "Branding",               icon: Paintbrush,   minRole: "org_admin",    testid: "stab-branding" },
  { to: "/settings/badge-templates",                  label: "Badge templates",        icon: IdCard,       minRole: "org_admin",    testid: "stab-badge-templates" },
  { to: "/settings/consent-documents",                label: "Consent documents",      icon: FileSignature,minRole: "org_admin",    testid: "stab-consent-documents" },
  { to: "/settings/watchlist",                        label: "Watchlist",              icon: ShieldAlert,  minRole: "org_admin",    testid: "stab-watchlist" },
  { to: "/settings/contractor-compliance",            label: "Contractor compliance",  icon: HardHat,      minRole: "site_manager", testid: "stab-contractor-compliance" },
  { to: "/settings/integrations",                     label: "Integrations",           icon: Plug,         minRole: "org_admin",    testid: "stab-integrations" },
  { to: "/settings/api-tokens",                       label: "API tokens",             icon: KeyRound,     minRole: "org_admin",    testid: "stab-api-tokens" },
  { to: "/settings/gdpr-privacy",                     label: "GDPR & privacy",         icon: ShieldCheck,  minRole: "org_admin",    testid: "stab-gdpr-privacy" },
  { to: "/settings/data",                             label: "Data tools",             icon: Trash2,       minRole: "org_admin",    testid: "stab-data-tools" },
  { to: "/admin/notifications-log",                   label: "Notifications log",      icon: ListChecks,   minRole: "org_admin",    testid: "stab-notifications-log" },
  { to: "/audit",                                     label: "Audit log",              icon: FileBarChart, minRole: "host",         testid: "stab-audit" },
  { to: "/billing",                                   label: "Billing",                icon: CreditCard,   minRole: "host",         testid: "stab-billing" },
];

export default function SettingsLayout({ children }) {
  const { user } = useAuth();
  const { noOrgSuperAdmin } = useOrgScope();
  const myLevel = ROLE_LEVEL[user?.role] || 0;
  const visible = TABS.filter((t) => myLevel >= ROLE_LEVEL[t.minRole]);

  return (
    <AppShell>
      <div className="flex flex-col lg:flex-row gap-6" data-testid="settings-layout">
        <aside className="lg:w-60 lg:flex-shrink-0" data-testid="settings-rail">
          <nav className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-2 sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto">
            <div className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-navy-400 dark:text-navy-300 inline-flex items-center gap-1.5">
              <SettingsIcon className="w-3 h-3" /> Settings
            </div>
            {visible.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  data-testid={t.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium mb-0.5 transition-colors ${
                      isActive
                        ? "bg-orange-500/10 text-orange-700 dark:text-orange-300"
                        : "text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>
        <div className="flex-1 min-w-0" data-testid="settings-content">
          {noOrgSuperAdmin ? <OrgScopeEmptyState /> : children}
        </div>
      </div>
    </AppShell>
  );
}
